import { useCallback, useEffect, useRef, useState } from 'react';
import { getPracticeClosures } from '../services/clousures/practiceClosureService';
import { PracticeClosure } from '../types/closures/types';

// Module-level cache: practiceId → closures (matches useCustomFields pattern)
const closureCache = new Map<string, PracticeClosure[]>();

const inFlightRequests = new Map<
  string,
  Promise<PracticeClosure[]>
>();

interface UseBatchedClosuresOptions {
  enabled?: boolean;
}

export const useBatchedClosures = (
  practiceIds: string[],
  options: UseBatchedClosuresOptions = {},
): {
  closuresByPractice: Record<string, PracticeClosure[]>;
  isLoading: boolean;
} => {
  const { enabled = true } = options;
  const [closuresByPractice, setClosuresByPractice] = useState<
    Record<string, PracticeClosure[]>
  >({});
  const [isLoading, setIsLoading] = useState(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const practiceIdsRef = useRef<string[]>(practiceIds);
  useEffect(() => {
    practiceIdsRef.current = practiceIds;
  }, [practiceIds]);

  const fetchBatch = useCallback(
    async (ids: string[], { revalidate = false }: { revalidate?: boolean } = {}) => {
      if (!ids.length || !enabled) return;

      // Serve cached entries immediately
      const cached: Record<string, PracticeClosure[]> = {};
      const misses: string[] = [];
      const staleHits: string[] = [];

      ids.forEach((id) => {
        if (closureCache.has(id)) {
          cached[id] = closureCache.get(id)!;
          // Painted from cache, then confirmed against the server. A cached entry
          // is otherwise believed forever, so anything that changed the closures
          // out from under it — another tab, another user, a closure added on the
          // detail page — left this list wrong until a browser refresh.
          if (revalidate) staleHits.push(id);
        } else {
          misses.push(id);
        }
      });

      if (Object.keys(cached).length > 0) {
        setClosuresByPractice((prev) => ({ ...prev, ...cached }));
      }

      const uncached = [...misses, ...staleHits];
      if (uncached.length === 0) return;

      // Only a real miss has nothing to show yet. Callers render a skeleton off
      // isLoading, so raising it for a revalidation would replace rows that are
      // already on screen with a spinner on every single mount — the opposite of
      // what painting from cache is for.
      const hasNothingToShow = misses.length > 0;

      // Deduplicate in-flight: same batchKey → same promise (concurrent mount dedup)
      const batchKey = uncached.sort().join(',');
      let fetchPromise = inFlightRequests.get(batchKey);
      if (hasNothingToShow) setIsLoading(true);

      if (!fetchPromise) {
        fetchPromise = getPracticeClosures({ practiceIds: uncached })
          .then((closures) => {
            const grouped: Record<string, PracticeClosure[]> = {};
            closures.forEach((c) => {
              if (c.practiceId) {
                grouped[c.practiceId] = grouped[c.practiceId] || [];
                grouped[c.practiceId].push(c);
              }
            });
            // Populate cache for each fetched ID (including empty arrays for no-closure practices)
            uncached.forEach((id) => {
              closureCache.set(id, grouped[id] || []);
            });
            inFlightRequests.delete(batchKey);
            return closures;
          })
          .catch((err) => {
            inFlightRequests.delete(batchKey);
            throw err;
          });
        inFlightRequests.set(batchKey, fetchPromise);
      }

      try {
        await fetchPromise;
        if (!mountedRef.current) return;
        const result: Record<string, PracticeClosure[]> = {};
        uncached.forEach((id) => {
          result[id] = closureCache.get(id) || [];
        });
        setClosuresByPractice((prev) => ({ ...prev, ...result }));
      } catch {
        if (!mountedRef.current) return;

        // Avoid per-item fallback bursts in list views when batched fetch fails:
        // this render still resolves the IDs to empty arrays so children do not
        // each fire their own request.
        //
        // The module cache is deliberately NOT written here. Nothing revalidates a
        // cached entry, so writing [] on a network blip would pin every practice in
        // the batch to "no closures" for the rest of the session. Leaving them
        // uncached costs one retry on the next mount.
        const failedResult: Record<string, PracticeClosure[]> = {};
        misses.forEach((id) => {
          failedResult[id] = [];
        });
        setClosuresByPractice((prev) => ({
          ...prev,
          ...failedResult,
        }));
      } finally {
        if (hasNothingToShow && mountedRef.current) setIsLoading(false);
      }
    },
    [enabled],
  );

  // Initial fetch on practiceIds change
  useEffect(() => {
    if (!enabled || practiceIds.length === 0) {
      setClosuresByPractice({});
      setIsLoading(false);
      return;
    }

    // Mounting a list is the moment to re-check: it is exactly when the user has
    // come back from somewhere that may have changed a closure.
    fetchBatch(practiceIds, { revalidate: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, practiceIds.join(','), fetchBatch]);

  // Scoped cache invalidation: only the affected practiceId is invalidated and re-fetched
  useEffect(() => {
    const handleUpdate = (e: Event) => {
      const detail = (e as CustomEvent<{ practiceId: string }>)
        .detail;
      if (
        detail?.practiceId &&
        practiceIdsRef.current.includes(detail.practiceId)
      ) {
        closureCache.delete(detail.practiceId);
        fetchBatch([detail.practiceId]);
      }
    };
    window.addEventListener('clinicClosureUpdated', handleUpdate);
    return () =>
      window.removeEventListener(
        'clinicClosureUpdated',
        handleUpdate,
      );
  }, [fetchBatch]);

  return { closuresByPractice, isLoading };
};
